import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { HomeworkActivityQuery, HomeworkActivityService, HomeworkActivityStoreModel } from '@examdojo/homework';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { QuestionAttemptEntityQuery, QuestionAttemptEntityService } from '@examdojo/question';
import { catchError, EMPTY, forkJoin, switchMap } from 'rxjs';
import { ExamdojoPlatformService } from '../../../shared/platform.service';

@Injectable()
export class SyncHomeworkActivityStateService {
  constructor(
    private readonly homeworkActivityService: HomeworkActivityService,
    private readonly homeworkActivityQuery: HomeworkActivityQuery,
    private readonly questionAttemptService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly platformService: ExamdojoPlatformService,
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
  ) {
    if (this.featureFlagService.get(ExamDojoFeatureFlag.Classes)) {
      this.syncStoreWhenBackOnline().pipe(takeUntilDestroyed()).subscribe();
    }
  }

  private syncStoreWhenBackOnline() {
    return this.platformService.trigger$.pipe(
      switchMap(() =>
        this.homeworkActivityService.fetchInProgress().pipe(
          switchMap(() => {
            const remoteOngoing = this.homeworkActivityQuery.getOngoingActivity();

            if (!remoteOngoing) {
              return EMPTY;
            }

            return forkJoin([
              this.syncQuestionAttemptStoreIfAnyChangeHasHappened(remoteOngoing),
              this.homeworkActivityService.fetchProgress(remoteOngoing.id),
            ]);
          }),
          catchError(() => EMPTY),
        ),
      ),
    );
  }

  private syncQuestionAttemptStoreIfAnyChangeHasHappened(activity: HomeworkActivityStoreModel) {
    const currentQuestionAttempt = this.questionAttemptQuery.getActive()?.attempt;

    if (
      activity.question_attempt_id &&
      currentQuestionAttempt &&
      activity.question_attempt_id === currentQuestionAttempt.id
    ) {
      return this.questionAttemptService.fetchQuestionAttempt(activity.question_attempt_id).pipe(
        switchMap((fetchedAttempt) => {
          if (fetchedAttempt?.updated_at !== currentQuestionAttempt.updated_at) {
            return this.questionAttemptService.fetchQuestionAttemptDetails(currentQuestionAttempt.id, {
              setActive: true,
            });
          }
          return EMPTY;
        }),
      );
    }

    return EMPTY;
  }
}
